// Where a pull request's changes can be seen running: preview deployments.
// Two sources, both already at hand or one small read away: GitHub
// deployments of the pull request's latest commits (PR_DEPLOYMENTS), and
// preview links that bots (Vercel, Netlify, Fly, a workflow's
// github-actions) post in the conversation. Each preview says honestly how
// it stands: ready at the head, still deploying, failed, or stale (made
// from an earlier commit, or posted before the head was pushed).
import type { TimelineItem } from '../api/queries';
import { isBot, pushTimes } from './timeline';
import { compareTimes } from './time';

/**
 * `unverified`: linked in a comment, but when the head was pushed is not
 * known, so nothing says whether it shows the head.
 */
export type PreviewStatus = 'ready' | 'pending' | 'unverified' | 'stale' | 'failed' | 'inactive';

export interface Preview {
  /** The page to open: the deployment's site, or its log when it failed without one; null when it has neither yet. */
  url: string | null;
  /** `url`'s host ('' without one). */
  host: string;
  /** The environment's name, or the login of who posted the link. */
  label: string;
  source: 'deployment' | 'comment';
  status: PreviewStatus;
  /** The commit deployed, when known. */
  sha: string | null;
  /** When it was deployed or posted (the comment's latest edit). */
  at: string;
  /** The deployment's log, when GitHub has one. */
  logUrl: string | null;
  /**
   * Why a stale preview is: deployed from an earlier commit, posted before
   * the head was pushed, or a deployment of the head pending so long it may
   * be stuck.
   */
  why?: 'older-commit' | 'before-push' | 'stuck';
}

/** A deployment as PR_DEPLOYMENTS reads it. */
export interface DeploymentNode {
  id: string;
  environment: string | null;
  createdAt: string;
  commitOid: string;
  state: string | null;
  latestStatus: { state: string; environmentUrl: string | null; logUrl: string | null; createdAt: string } | null;
}

/**
 * One of the pull request's latest commits and its deployments, as
 * `fetchDeployments` hands them on: every list in place, none where
 * GitHub withheld them (a partial error, as when the token may not read
 * the repository's deployments), and no empty entries.
 */
export interface DeploymentCommit {
  oid: string;
  deployments: { nodes: DeploymentNode[] };
}

const READY = new Set(['SUCCESS', 'ACTIVE']);
const FAILED = new Set(['FAILURE', 'ERROR']);
const PENDING = new Set(['IN_PROGRESS', 'QUEUED', 'PENDING', 'WAITING']);
/** How long a deployment may stay pending before it is taken to be stuck (stale). */
const PENDING_MS = 60 * 60_000;
/** Environments that are the product, not a preview of the pull request. */
const PRODUCTION = /^(prod|production|github-pages)$|(^|[\s_-])(prod|production)($|[\s_-])/i;

/** An http(s) URL as written, normalized, or null. */
function webUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  try {
    const url = new URL(u);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

const hostOf = (u: string | null) => (u ? new URL(u).hostname.replace(/^www\./, '') : '');

const sameSha = (a: string, b: string) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Each non-production environment's preview: its latest deployment of the
 * head `head` (ready with its site, failed with its log, deploying, or
 * inactive; still pending after PENDING_MS, stale), else, when the head's
 * gave nothing to open, its latest successful one of an earlier commit,
 * shown stale. Deployments with nothing to open (a ready one without a
 * site, an abandoned one) are left out. Environments keep the order they
 * first appear in. `now` is the clock pending deployments are read against.
 */
export function deploymentPreviews(commits: readonly DeploymentCommit[], head: string, now = Date.now()): Preview[] {
  const byEnv = new Map<string, { head: DeploymentNode[]; older: DeploymentNode[] }>();
  for (const c of commits)
    for (const d of c.deployments.nodes) {
      const env = d.environment ?? 'deployment';
      if (PRODUCTION.test(env)) continue;
      let e = byEnv.get(env);
      if (!e) byEnv.set(env, (e = { head: [], older: [] }));
      (sameSha(d.commitOid || c.oid, head) ? e.head : e.older).push(d);
    }
  const latest = (list: DeploymentNode[]) => [...list].sort((a, b) => compareTimes(a.createdAt, b.createdAt)).at(-1);
  const out: Preview[] = [];
  for (const [env, e] of byEnv) {
    const d = latest(e.head);
    const base = (n: DeploymentNode, status: PreviewStatus, url: string | null, why?: Preview['why']): Preview => ({
      ...(why ? { why } : {}),
      url,
      host: hostOf(url),
      label: env,
      source: 'deployment',
      status,
      sha: n.commitOid,
      at: n.latestStatus?.createdAt ?? n.createdAt,
      logUrl: webUrl(n.latestStatus?.logUrl),
    });
    if (d) {
      const state = d.latestStatus?.state ?? d.state ?? '';
      const site = webUrl(d.latestStatus?.environmentUrl);
      const since = Date.parse(d.latestStatus?.createdAt ?? d.createdAt);
      const shown =
        READY.has(state) && site
          ? base(d, 'ready', site)
          : FAILED.has(state)
            ? base(d, 'failed', site ?? webUrl(d.latestStatus?.logUrl))
            : PENDING.has(state)
              ? now - since > PENDING_MS
                ? base(d, 'stale', site, 'stuck')
                : base(d, 'pending', site)
              : state === 'INACTIVE' && site
                ? base(d, 'inactive', site)
                : null;
      if (shown) {
        out.push(shown);
        continue;
      }
    }
    const older = latest(e.older.filter((n) => READY.has(n.latestStatus?.state ?? n.state ?? '') || (n.latestStatus?.state ?? n.state) === 'INACTIVE').filter((n) => webUrl(n.latestStatus?.environmentUrl)));
    if (older) out.push(base(older, 'stale', webUrl(older.latestStatus?.environmentUrl), 'older-commit'));
  }
  return out;
}

/** Hosts that serve preview deployments. */
const PREVIEW_HOST = /\.(vercel\.app|netlify\.app|fly\.dev|pages\.dev|onrender\.com|herokuapp\.com|surge\.sh|railway\.app|up\.railway\.app|web\.app|firebaseapp\.com|amplifyapp\.com|azurestaticapps\.net|ngrok\.app|ngrok\.io)$/i;
/** Hosts whose pages are dashboards and logs, not previews. */
const NOT_PREVIEW = /(^|\.)(github\.com|githubusercontent\.com|vercel\.com|app\.netlify\.com|fly\.io|dashboard\.render\.com|railway\.com|console\.firebase\.google\.com)$/i;
/** A person's line that names a preview outright: "Preview: …", "Preview deployed: …". */
const PREVIEW_LINE = /^\W*preview(\s+(deployed|deployment|url|link|ready|available|is\s+up))?\s*(at|[:：-])/i;
const URLS = /\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s<>()[\]|"'`]+)/g;

/**
 * Whose comments may name a preview, and how:
 * - `deploy-bot`: a deploy service's bot (DEPLOY_BOTS): a link on a line
 *   that says preview or deploy, or to a preview host;
 * - `actions`: GitHub Actions' bot, which any workflow speaks for: only a
 *   link to a preview host;
 * - `person`: the pull request's author or a collaborator: only a link on
 *   a line that names a preview outright ("Preview: …").
 */
export type Speaker = 'deploy-bot' | 'actions' | 'person';

/*
 * Trusting `github-actions` for links to preview hosts: any of the
 * repository's own workflows posts as it, including `pull_request_target`
 * workflows that may echo the pull request's text, so a link to a site
 * someone controls on a preview host could pass. Accepted: only the
 * repository's own workflow code can post as it, and the host must be a
 * preview service's; a stranger's comment never counts.
 */
/** Deploy services' bots, by login (GitHub gives a bot's login with or without `[bot]`). */
const DEPLOY_BOTS = new Set(['vercel', 'netlify', 'cloudflare-workers-and-pages', 'railway-app', 'render']);
/** Associations whose comments are the repository's own word. */
const COLLABORATOR = new Set(['OWNER', 'MEMBER', 'COLLABORATOR']);

/**
 * How a comment's author may name previews, or null when they may not:
 * anyone else's links (a stranger, a bot quoting text) are never previews.
 */
export function speaker(it: Extract<TimelineItem, { __typename: 'IssueComment' }>, prAuthor: string | null): Speaker | null {
  const login = it.author?.login;
  if (!login) return null;
  if (isBot(it.author)) {
    const name = login.toLowerCase().replace(/\[bot\]$/, '');
    return DEPLOY_BOTS.has(name) ? 'deploy-bot' : name === 'github-actions' ? 'actions' : null;
  }
  if (prAuthor && login.toLowerCase() === prAuthor.toLowerCase()) return 'person';
  return it.authorAssociation && COLLABORATOR.has(it.authorAssociation) ? 'person' : null;
}

/** Preview links in a comment's body, each with its line. */
function previewLinks(body: string, who: Speaker): { url: string; line: string }[] {
  const out: { url: string; line: string }[] = [];
  const seen = new Set<string>();
  for (const line of body.replace(/\r\n?/g, '\n').split('\n')) {
    const said = who === 'deploy-bot' ? /preview|deploy/i.test(line) : who === 'person' ? PREVIEW_LINE.test(line) : false;
    for (const m of line.matchAll(URLS)) {
      const url = webUrl((m[1] ?? m[2]).replace(/[.,;:!?]+$/, ''));
      if (!url || seen.has(url)) continue;
      const host = new URL(url).hostname;
      if (NOT_PREVIEW.test(host)) continue;
      if (!(said || (who !== 'person' && PREVIEW_HOST.test(host)))) continue;
      seen.add(url);
      out.push({ url, line });
    }
  }
  return out;
}

/** The preview links in a comment's body, as `who` may name them (see Speaker). Links to GitHub and to hosts' dashboards are never previews. */
export function previewUrls(body: string, who: Speaker): string[] {
  return previewLinks(body, who).map((l) => l.url);
}

/**
 * The previews the conversation's comments link, from those who may name
 * them (`speaker`; `prAuthor` is the pull request's author), one per URL,
 * as of its latest mention: failed when that line says so, stale when the
 * comment was last edited before the head was pushed (`pushedAt`), and
 * unverified when that time is unknown.
 */
export function commentPreviews(timeline: readonly TimelineItem[], pushedAt: string | null, prAuthor: string | null): Preview[] {
  const out = new Map<string, Preview>();
  for (const it of timeline) {
    if (it.__typename !== 'IssueComment') continue;
    const who = speaker(it, prAuthor);
    if (!who) continue;
    const at = it.lastEditedAt ?? it.createdAt;
    for (const { url, line } of previewLinks(it.body, who)) {
      const prev = out.get(url);
      if (prev && compareTimes(prev.at, at) > 0) continue;
      const status: PreviewStatus = /fail|error|❌/i.test(line) ? 'failed' : !pushedAt ? 'unverified' : compareTimes(at, pushedAt) < 0 ? 'stale' : 'ready';
      out.set(url, { url, host: hostOf(url), label: it.author?.login ?? 'ghost', source: 'comment', status, sha: null, at, logUrl: null, ...(status === 'stale' ? { why: 'before-push' as const } : {}) });
    }
  }
  return [...out.values()];
}

/** When the head `head` reached GitHub, as well as the timeline can tell: its force-push, else its push time. */
export function headPushedAt(timeline: readonly TimelineItem[], head: string): string | null {
  let at: string | null = pushTimes([...timeline]).get(head) ?? null;
  for (const it of timeline)
    if (it.__typename === 'HeadRefForcePushedEvent' && it.afterCommit && sameSha(it.afterCommit.oid, head) && (!at || compareTimes(it.createdAt, at) > 0)) at = it.createdAt;
  return at;
}

const RANK: Record<PreviewStatus, number> = { ready: 0, pending: 1, unverified: 2, stale: 3, failed: 4, inactive: 5 };
const urlKey = (u: string | null) => (u ? u.replace(/\/+$/, '') : null);

/**
 * Every preview: deployments' (`deploymentPreviews`), then comments' whose
 * URL no deployment has, ready ones first (then deploying, unverified,
 * stale, failed, inactive), deployments before comments within each.
 */
export function previews(
  commits: readonly DeploymentCommit[],
  timeline: readonly TimelineItem[],
  head: string,
  pushedAt: string | null,
  prAuthor: string | null,
  now = Date.now(),
): Preview[] {
  const deployed = deploymentPreviews(commits, head, now);
  const have = new Set(deployed.map((p) => urlKey(p.url)).filter((k): k is string => k !== null));
  const posted = commentPreviews(timeline, pushedAt, prAuthor).filter((p) => !have.has(urlKey(p.url) ?? ''));
  return [...deployed, ...posted].map((p, i) => [p, i] as const).sort(([a, i], [b, j]) => RANK[a.status] - RANK[b.status] || i - j).map(([p]) => p);
}

/** A preview's status as words, after its name: "ready", "deploying", "stale", "failed", "inactive". */
export function previewStatusText(p: Preview): string {
  return p.status === 'pending' ? 'deploying' : p.status === 'unverified' ? 'not known to be current' : p.status;
}

/** Hours a pending deployment may last before it is called stuck, as text. */
const PENDING_TEXT = `${PENDING_MS / 3_600_000}h+`;

/**
 * Where a preview came from and how it stands, in words: the one
 * description its tooltips and screen-reader text use.
 */
export function previewWhy(p: Preview): string {
  const what = p.source === 'deployment' ? `${p.label} deployment` : `linked by ${p.label}`;
  const why =
    p.status === 'stale'
      ? p.why === 'stuck'
        ? `, pending for ${PENDING_TEXT}, may be stuck`
        : p.why === 'older-commit' && p.sha
          ? `, deployed from ${p.sha.slice(0, 7)}, not the latest commit`
          : ', posted before the latest push'
      : p.status === 'failed'
        ? p.url && p.url === p.logUrl
          ? ', failed: opens its log'
          : ', failed'
        : p.status === 'pending'
          ? ', still deploying'
          : p.status === 'inactive'
            ? ', no longer active'
            : p.status === 'unverified'
              ? ', not known to be current: when the head was pushed is not known'
              : '';
  return what + why;
}
